/**
 * The briefing (13-09, mock `.brief`): the Melbourne date, the serif greeting with the first name
 * in italic, the count line (overdue in a gentle amber) and one calm line about the person's patch.
 * The lotus watermark sits at ~.14 opacity, half off the edge. It names no person or client
 * beyond the reader: refs and counts only. 13-29: the records line ("3 records waiting on you,
 * 1 decision due") sits after the count line when anything is waiting.
 */

import { Link } from 'react-router-dom';
import { countParts, firstNameOf, melbourneLongDate, patchLine, salutation } from '../../lib/briefing';
import type { PatchMetrics } from '../../lib/briefing';
import { lensFor } from '../../lib/actionAuthority';
import type { ActionRow, Actor } from '../../types/actionRegister';
import styles from './today.module.css';

export interface BriefingProps {
  actor: Actor | null;
  now: number;
  /** The queue as shown (hidden cards already out), or null while it loads. */
  queue: readonly Pick<ActionRow, 'dueDate'>[] | null;
  patch: PatchMetrics | null;
  /** 13-29: `recordsLine(groups)`, counts only; "" when no register record is waiting. */
  records?: string;
}

export default function Briefing({ actor, now, queue, patch, records = '' }: BriefingProps) {
  const name = firstNameOf(actor?.displayName);
  const parts = queue ? countParts(queue, new Date(now)) : null;
  const line = actor ? patchLine(lensFor(actor), patch) : '';
  const oversight = !!actor?.roles.includes('Oversight');

  return (
    <div className={styles.brief}>
      <div className={styles.briefBg} aria-hidden="true">
        <div className={styles.lotus}>
          <img src="/logos/harmony-flower.png" alt="" />
        </div>
      </div>
      <p className={styles.date}>{melbourneLongDate(new Date(now))}</p>
      <h1 className={styles.greet} id="greet">
        {salutation(new Date(now))}
        {name && (
          <>
            , <em>{name}</em>
          </>
        )}
      </h1>
      <p className={styles.countline} aria-live="polite">
        {parts === null ? (
          ''
        ) : parts.count === 0 ? (
          'Nothing left for today.'
        ) : (
          <>
            {parts.count} {parts.count === 1 ? 'thing' : 'things'} for you
            {parts.overdue > 0 && (
              <>
                , <span className={styles.od}>{parts.overdue} overdue</span>
              </>
            )}
          </>
        )}
      </p>
      {records && (
        <p className={styles.recordsLine}>
          <a className={styles.link} href="#records-waiting">
            {records}
          </a>
        </p>
      )}
      {line && (
        <p className={styles.patch}>
          <span className={styles.dot} aria-hidden="true" />
          <span>{line}</span>
          {oversight && (
            <>
              <Link className={styles.link} to="/release">
                Open the release sitting
              </Link>
              <Link className={styles.link} to="/verify">
                Open Verify
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}
